/**
 * Configuration — Pricing Plan workflow helpers.
 */

import {
  SAVE_FAILURE_MESSAGE,
  SAVE_NO_CHANGE_MESSAGE,
  SAVE_SUCCESS_MESSAGE,
  SOURCE_PRICE_CHANGE_STATES,
  UPGRADE_CHARGE_OPTIONS,
  UPGRADE_TIMING_OPTIONS,
  DOWNGRADE_TIMING_OPTIONS,
  CANCELLATION_TIMING_OPTIONS,
  VISIBILITY_OPTIONS,
  PAYMENT_METHOD_DURING_TRIAL,
  VALIDATION_MESSAGES,
  buildSourceSnapshot,
  deriveSourcePriceChangeState,
  enrichConfigurationRecord,
  generateNextConfigurationId,
  getDefaultConfigurationFields,
  getPlanKeyFromManagementPlan,
  toConfigurationForm,
} from './plansConfig';
import { hasPendingPriceChange } from './pricingPlanManagementData';
import { emitSnapshotHistoryEntry } from './changeHistoryEmitters';
import { getActorDisplayName } from './teamConfig';

export { SAVE_SUCCESS_MESSAGE, SAVE_FAILURE_MESSAGE, SAVE_NO_CHANGE_MESSAGE, VALIDATION_MESSAGES };

export function buildPricingPlanConfigActor(auth) {
  if (!auth) return { name: 'System', accountId: '—' };
  return { name: getActorDisplayName(auth), accountId: '—' };
}

function isWholeNumber(value) {
  const raw = String(value ?? '').trim();
  if (!raw) return false;
  if (raw.includes('.') || raw.includes('e') || raw.includes('E')) return false;
  const num = Number(raw);
  return Number.isInteger(num);
}

function isPositiveWholeNumber(value) {
  return isWholeNumber(value) && Number(value) >= 1;
}

function isNonNegativeWholeNumber(value) {
  return isWholeNumber(value) && Number(value) >= 0;
}

export function collectFieldErrors(formData, configurations, managementPlan, { isNew = false } = {}) {
  const errors = {};
  const planKey =
    formData.planKey || getPlanKeyFromManagementPlan(managementPlan) || '';

  if (isNew || !formData.planId) {
    if (!formData.planId) {
      errors.planId = VALIDATION_MESSAGES.sourcePlanRequired;
    } else {
      const duplicate = (configurations || []).find(
        (config) => config.planId === formData.planId && config.id !== formData.id,
      );
      if (duplicate) errors.planId = VALIDATION_MESSAGES.sourcePlanExists;
    }
  }

  if (Array.isArray(formData.conditions) && formData.conditions.length > 0) {
    errors.conditions = VALIDATION_MESSAGES.conditionsNotSupported;
  }

  if (planKey === 'pro') {
    if (formData.trialIncluded && formData.trialIncluded !== 'No') {
      errors.trialIncluded = VALIDATION_MESSAGES.proTrialNotSupported;
    }
  } else if (planKey === 'basic') {
    if (!formData.trialIncluded) {
      errors.trialIncluded = VALIDATION_MESSAGES.requiredFields;
    } else if (formData.trialIncluded === 'Yes') {
      if (!isPositiveWholeNumber(formData.trialLengthDays)) {
        errors.trialLengthDays = VALIDATION_MESSAGES.trialLengthInvalid;
      }
    }
  }

  if (formData.postsAllowed === '' || formData.postsAllowed == null) {
    errors.postsAllowed = VALIDATION_MESSAGES.wholeNumberZeroOrGreater;
  } else if (!isNonNegativeWholeNumber(formData.postsAllowed)) {
    errors.postsAllowed = VALIDATION_MESSAGES.wholeNumberZeroOrGreater;
  }

  if (formData.featuredPostsAllowed === '' || formData.featuredPostsAllowed == null) {
    errors.featuredPostsAllowed = VALIDATION_MESSAGES.wholeNumberZeroOrGreater;
  } else if (!isNonNegativeWholeNumber(formData.featuredPostsAllowed)) {
    errors.featuredPostsAllowed = VALIDATION_MESSAGES.wholeNumberZeroOrGreater;
  }

  if (formData.quotesAllowed === '' || formData.quotesAllowed == null) {
    errors.quotesAllowed = VALIDATION_MESSAGES.quotesAllowedInvalid;
  } else if (!isNonNegativeWholeNumber(formData.quotesAllowed)) {
    errors.quotesAllowed = VALIDATION_MESSAGES.quotesAllowedInvalid;
  }

  if (!formData.contactDetailsVisibility) {
    errors.contactDetailsVisibility = VALIDATION_MESSAGES.contactDetailsRequired;
  } else if (!VISIBILITY_OPTIONS.includes(formData.contactDetailsVisibility)) {
    errors.contactDetailsVisibility = VALIDATION_MESSAGES.unavailableValue;
  }

  if (!formData.allowedExternalLinksVisibility) {
    errors.allowedExternalLinksVisibility = VALIDATION_MESSAGES.externalLinksRequired;
  } else if (!VISIBILITY_OPTIONS.includes(formData.allowedExternalLinksVisibility)) {
    errors.allowedExternalLinksVisibility = VALIDATION_MESSAGES.unavailableValue;
  }

  if (!isPositiveWholeNumber(formData.gracePeriodDays)) {
    errors.gracePeriodDays = VALIDATION_MESSAGES.gracePeriodInvalid;
  }

  if (planKey === 'basic') {
    if (!formData.upgradeTiming || !UPGRADE_TIMING_OPTIONS.includes(formData.upgradeTiming)) {
      errors.upgradeTiming = VALIDATION_MESSAGES.planChangeIncomplete;
    }
    if (
      !formData.upgradeChargeMethod ||
      !UPGRADE_CHARGE_OPTIONS.includes(formData.upgradeChargeMethod)
    ) {
      errors.upgradeChargeMethod = VALIDATION_MESSAGES.planChangeIncomplete;
    }
  }

  if (planKey === 'pro') {
    if (!formData.downgradeTiming || !DOWNGRADE_TIMING_OPTIONS.includes(formData.downgradeTiming)) {
      errors.downgradeTiming = VALIDATION_MESSAGES.planChangeIncomplete;
    }
  }

  if (
    !formData.cancellationTiming ||
    !CANCELLATION_TIMING_OPTIONS.includes(formData.cancellationTiming)
  ) {
    errors.cancellationTiming = VALIDATION_MESSAGES.cancellationRequired;
  }

  return errors;
}

function configurableSnapshot(form) {
  const planKey = form.planKey || '';
  const trialYes = planKey === 'basic' && form.trialIncluded === 'Yes';
  return {
    planId: form.planId || '',
    trialIncluded: planKey === 'pro' ? 'No' : form.trialIncluded || '',
    trialLengthDays: trialYes ? Number(form.trialLengthDays) : null,
    paymentMethodDuringTrial: trialYes ? PAYMENT_METHOD_DURING_TRIAL : '',
    postsAllowed: Number(form.postsAllowed),
    featuredPostsAllowed: Number(form.featuredPostsAllowed),
    quotesAllowed: Number(form.quotesAllowed),
    contactDetailsVisibility: form.contactDetailsVisibility || '',
    allowedExternalLinksVisibility: form.allowedExternalLinksVisibility || '',
    gracePeriodDays: Number(form.gracePeriodDays),
    upgradeTiming: planKey === 'basic' ? form.upgradeTiming || UPGRADE_TIMING_OPTIONS[0] : '',
    upgradeChargeMethod:
      planKey === 'basic' ? form.upgradeChargeMethod || UPGRADE_CHARGE_OPTIONS[0] : '',
    downgradeTiming: planKey === 'pro' ? form.downgradeTiming || DOWNGRADE_TIMING_OPTIONS[0] : '',
    cancellationTiming: form.cancellationTiming || '',
    conditions: Array.isArray(form.conditions) ? form.conditions : [],
  };
}

export function configFormHasChanges(current, baseline, { isNew = false } = {}) {
  if (isNew || !baseline) return true;
  return JSON.stringify(configurableSnapshot(current)) !== JSON.stringify(configurableSnapshot(baseline));
}

export function getPublishedConfiguration(config) {
  if (!config) return null;
  return toConfigurationForm(config);
}

function resolveOperationalSchedule(formData, managementPlan, previousSchedule) {
  const state = deriveSourcePriceChangeState(
    { ...formData, operationalPriceSchedule: previousSchedule },
    managementPlan,
  );

  if (state === SOURCE_PRICE_CHANGE_STATES.CANCELLATION_PENDING) {
    return null;
  }
  if (state === SOURCE_PRICE_CHANGE_STATES.PENDING_CONFIRMATION && hasPendingPriceChange(managementPlan)) {
    return {
      pendingMonthlyCost: Number(managementPlan.pendingMonthlyCost),
      effectiveFrom: managementPlan.effectiveFrom,
    };
  }
  if (state === SOURCE_PRICE_CHANGE_STATES.SCHEDULED) {
    return previousSchedule
      ? { ...previousSchedule }
      : {
          pendingMonthlyCost: Number(managementPlan.pendingMonthlyCost),
          effectiveFrom: managementPlan.effectiveFrom,
        };
  }
  return previousSchedule || null;
}

function buildHistoryEntry({ action, previousValues, newValues, actorName, actorId, nowIso }) {
  return {
    action,
    previousValues,
    newValues,
    by: actorName,
    personId: actorId || '—',
    on: nowIso,
  };
}

/**
 * Merge after Save. Unchanged Save is a no-op (returns same list + noChange).
 * Does not revalidate source price Effective From against the config save date.
 */
export function mergeConfigurationsAfterSave(
  allConfigs,
  formData,
  publishedBaseline,
  managementPlan,
  actor,
  { isNew = false } = {},
) {
  const fieldErrors = collectFieldErrors(formData, allConfigs, managementPlan, { isNew });
  if (Object.keys(fieldErrors).length) {
    return {
      configurations: allConfigs,
      success: false,
      message: SAVE_FAILURE_MESSAGE,
      errors: fieldErrors,
      noChange: false,
    };
  }

  if (!isNew && !configFormHasChanges(formData, publishedBaseline, { isNew: false })) {
    const priceState = deriveSourcePriceChangeState(formData, managementPlan);
    const needsScheduleSync =
      priceState === SOURCE_PRICE_CHANGE_STATES.PENDING_CONFIRMATION ||
      priceState === SOURCE_PRICE_CHANGE_STATES.CANCELLATION_PENDING;
    if (!needsScheduleSync) {
      return {
        configurations: allConfigs,
        success: true,
        message: SAVE_NO_CHANGE_MESSAGE,
        errors: {},
        noChange: true,
        config: publishedBaseline ? enrichConfigurationRecord(publishedBaseline, managementPlan) : null,
      };
    }
  }

  const nowIso = new Date().toISOString();
  const actorName = actor?.name || 'System';
  const actorId = actor?.accountId || '—';
  const planKey =
    formData.planKey ||
    getPlanKeyFromManagementPlan(managementPlan) ||
    publishedBaseline?.planKey ||
    '';
  const defaults = getDefaultConfigurationFields(planKey);
  const source = buildSourceSnapshot(managementPlan);
  const configId = isNew
    ? generateNextConfigurationId(allConfigs)
    : formData.id || publishedBaseline?.id;

  const previousSchedule =
    formData.operationalPriceSchedule ?? publishedBaseline?.operationalPriceSchedule ?? null;
  const operationalPriceSchedule = resolveOperationalSchedule(
    formData,
    managementPlan,
    previousSchedule,
  );

  const trialYes = planKey === 'basic' && formData.trialIncluded === 'Yes';

  const nextValues = {
    id: configId,
    planId: managementPlan?.id || formData.planId,
    planKey,
    planName: managementPlan?.planName || formData.planName,
    source,
    trialIncluded: planKey === 'pro' ? 'No' : formData.trialIncluded || defaults.trialIncluded,
    trialLengthDays: trialYes ? Number(formData.trialLengthDays) : null,
    paymentMethodDuringTrial: trialYes ? PAYMENT_METHOD_DURING_TRIAL : '',
    postsAllowed: Number(formData.postsAllowed),
    featuredPostsAllowed: Number(formData.featuredPostsAllowed),
    quotesAllowed: Number(formData.quotesAllowed),
    contactDetailsVisibility: formData.contactDetailsVisibility,
    allowedExternalLinksVisibility: formData.allowedExternalLinksVisibility,
    gracePeriodDays: Number(formData.gracePeriodDays),
    upgradeTiming: planKey === 'basic' ? formData.upgradeTiming || UPGRADE_TIMING_OPTIONS[0] : '',
    upgradeChargeMethod:
      planKey === 'basic' ? formData.upgradeChargeMethod || UPGRADE_CHARGE_OPTIONS[0] : '',
    downgradeTiming: planKey === 'pro' ? formData.downgradeTiming || DOWNGRADE_TIMING_OPTIONS[0] : '',
    cancellationTiming: formData.cancellationTiming || CANCELLATION_TIMING_OPTIONS[0],
    conditions: [],
    operationalPriceSchedule,
    createdOn: publishedBaseline?.createdOn || nowIso,
    createdBy: publishedBaseline?.createdBy || actorName,
    lastUpdatedOn: nowIso,
    lastUpdatedBy: actorName,
  };

  const previousSnapshot = publishedBaseline ? configurableSnapshot(publishedBaseline) : null;
  const nextSnapshot = configurableSnapshot({ ...nextValues, planKey });
  const historyEntry = buildHistoryEntry({
    action: isNew ? 'Created' : 'Updated',
    previousValues: previousSnapshot,
    newValues: nextSnapshot,
    actorName,
    actorId,
    nowIso,
  });

  const record = enrichConfigurationRecord(
    {
      ...nextValues,
      changeHistory: [...(publishedBaseline?.changeHistory || []), historyEntry],
    },
    managementPlan,
  );

  emitSnapshotHistoryEntry({
    localEntry: historyEntry,
    module: 'Pricing Plans',
    sourceModuleRef: 'pricing_plans',
    changedItem: record.planName || record.name || configId,
    itemId: configId || record.id,
    changeReason: formData.changeReason,
  });

  const configurations =
    !isNew && allConfigs.some((config) => config.id === configId)
      ? allConfigs.map((config) => (config.id === configId ? record : config))
      : [...allConfigs, record];

  return {
    configurations,
    config: record,
    success: true,
    message: SAVE_SUCCESS_MESSAGE,
    errors: {},
    noChange: false,
  };
}

export function canSaveConfiguration(formData, baseline, managementPlan) {
  const editableChanged = configFormHasChanges(formData, baseline);
  const priceState = deriveSourcePriceChangeState(formData, managementPlan);
  const needsPriceSync =
    priceState === SOURCE_PRICE_CHANGE_STATES.PENDING_CONFIRMATION ||
    priceState === SOURCE_PRICE_CHANGE_STATES.CANCELLATION_PENDING;
  return editableChanged || needsPriceSync;
}
